import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { useBilling, useInvoices } from '../lib/queries'
import { PageHead, Skel } from '../components/ui'
import { money, thaiDate } from '../lib/format'

const statusLabel: Record<string, string> = {
  trialing: 'ทดลองใช้', active: 'ใช้งาน', past_due: 'ค้างชำระ', canceled: 'ยกเลิก',
  open: 'รอชำระ', paid: 'ชำระแล้ว', draft: 'ฉบับร่าง', void: 'ยกเลิก',
}

export function Billing() {
  const { me } = useAuth()
  const canIssue = me?.role === 'owner' || me?.role === 'admin'
  const b = useBilling()
  const inv = useInvoices()
  const qc = useQueryClient()
  const issue = useMutation({
    mutationFn: () => apiWrite('/v1/billing/invoices', 'POST', {}),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['invoices'] }); void qc.invalidateQueries({ queryKey: ['billing'] }) },
  })

  const snap = b.data
  const q = snap?.quote
  const pct = q && q.included_docs > 0 ? Math.min(100, Math.round((q.used_docs / q.included_docs) * 100)) : 0

  return (
    <>
      <PageHead title="การเรียกเก็บเงิน" sub="คิดตามการใช้งานจริง · ใบแจ้งหนี้มี VAT 7%"
        actions={canIssue ? <button className="btn btn-primary" disabled={issue.isPending} onClick={() => issue.mutate()}>
          {issue.isPending ? 'กำลังออก…' : 'ออกใบแจ้งหนี้รอบนี้'}
        </button> : undefined} />

      {b.isLoading || !snap || !q ? <div className="stack"><Skel h={140} /><Skel h={90} /></div> : (
        <>
          <div className="grid-main">
            <div className="card card-pad">
              <div className="card-label">แพ็กเกจปัจจุบัน</div>
              <div className="row" style={{ marginTop: 12, alignItems: 'baseline', gap: 14 }}>
                <span className="display-m">{snap.plan.name}</span>
                <span className="chip gold">{statusLabel[snap.subscription.status] ?? snap.subscription.status}</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
                  รอบ {thaiDate(snap.subscription.current_period_start)} — {thaiDate(snap.subscription.current_period_end)}
                </span>
              </div>
              <div className="row" style={{ marginTop: 22, gap: 28 }}>
                <div>
                  <div className="eyebrow">ค่าบริการรายเดือน</div>
                  <div className="heading-l tnum" style={{ marginTop: 6 }}>{money(snap.plan.price_satang)}</div>
                </div>
                <div>
                  <div className="eyebrow">เอกสารในแพ็กเกจ</div>
                  <div className="heading-l tnum" style={{ marginTop: 6 }}>{snap.plan.included_docs.toLocaleString('th-TH')}</div>
                </div>
                <div>
                  <div className="eyebrow">เกินแพ็กเกจ</div>
                  <div className="heading-l tnum" style={{ marginTop: 6 }}>{money(snap.plan.overage_satang)}<span className="stat-unit">/เอกสาร</span></div>
                </div>
              </div>

              <div style={{ marginTop: 26 }}>
                <div className="row" style={{ fontSize: 12 }}>
                  <span className="dim">ใช้ไป {q.used_docs.toLocaleString('th-TH')} จาก {q.included_docs.toLocaleString('th-TH')} เอกสาร</span>
                  <span className="tnum" style={{ marginLeft: 'auto' }}>{pct}%</span>
                </div>
                <div className="bar" style={{ height: 6, marginTop: 8 }}>
                  <i style={{ width: `${Math.max(2, pct)}%`, background: pct > 90 ? 'var(--color-amber)' : 'var(--color-gold)' }} />
                </div>
              </div>
            </div>

            <div className="card card-pad">
              <div className="card-label">ประมาณการรอบนี้</div>
              <dl className="kv" style={{ marginTop: 16, gridTemplateColumns: '1fr auto' }}>
                <dt>ค่าบริการฐาน</dt><dd className="tnum" style={{ textAlign: 'right' }}>{money(q.base_satang)}</dd>
                <dt>ส่วนเกิน ({q.overage_docs} เอกสาร)</dt><dd className="tnum" style={{ textAlign: 'right' }}>{money(q.overage_satang)}</dd>
                <dt>ก่อนภาษี</dt><dd className="tnum" style={{ textAlign: 'right' }}>{money(q.subtotal_satang)}</dd>
                <dt>VAT 7%</dt><dd className="tnum" style={{ textAlign: 'right' }}>{money(q.vat_satang)}</dd>
              </dl>
              <div className="rule" style={{ margin: '14px 0' }} />
              <div className="row">
                <span className="dim" style={{ fontSize: 12 }}>รวมทั้งสิ้น</span>
                <span className="display-m tnum" style={{ marginLeft: 'auto' }}>{money(q.total_satang)}</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            <div className="card-label" style={{ marginBottom: 10 }}>ใบแจ้งหนี้</div>
            {(inv.data ?? []).length === 0 ? (
              <div className="card card-pad dim" style={{ fontSize: 12.5 }}>ยังไม่มีใบแจ้งหนี้ — กด “ออกใบแจ้งหนี้รอบนี้” เพื่อสร้างฉบับแรก</div>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>เลขที่</th><th>รอบ</th><th>แพ็กเกจ</th><th>ก่อนภาษี</th><th>VAT</th><th>รวม</th><th>ครบกำหนด</th><th>สถานะ</th></tr></thead>
                  <tbody>
                    {(inv.data ?? []).map((i) => (
                      <tr key={i.id} style={{ cursor: 'default' }}>
                        <td className="mono">{i.number}</td>
                        <td className="dim">{thaiDate(i.period_start)} — {thaiDate(i.period_end)}</td>
                        <td>{i.plan_id}</td>
                        <td className="num">{money(i.subtotal_satang)}</td>
                        <td className="num dim">{money(i.vat_satang)}</td>
                        <td className="num">{money(i.total_satang)}</td>
                        <td className="dim">{thaiDate(i.due_at)}</td>
                        <td><span className={`chip${i.status === 'paid' ? ' gold' : ''}`}>{statusLabel[i.status] ?? i.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
